import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { TrendingUp, TrendingDown, Shield, AlertTriangle, PieChart as PieChartIcon, BarChart3, ExternalLink, Lightbulb, X } from 'lucide-react';
import { authService } from '../../services/auth';
import { insightsService } from '../../services/insights';
import DashboardTopBar from './dashboard-topbar';
import PortfolioLineChart, { DataPoint } from '../ui/line-chart';

// Main Dashboard Component
const PortfolioDashboard: React.FC = () => {
  const [holdings, setHoldings] = useState<Holding[]>([]);
  const [profile, setProfile] = useState<InvestorProfile | null>(null);
  const [signals, setSignals] = useState<MacroSignal[]>([]);
  const [riskAlerts, setRiskAlerts] = useState<RiskAlert[]>([]);
  const [opportunities, setOpportunities] = useState<OpportunityInsight[]>([]);
  const [dailyBriefing, setDailyBriefing] = useState<DailyBriefing | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<'portfolio' | 'briefing' | 'alerts' | 'news'>('portfolio');
  const [selectedSignal, setSelectedSignal] = useState<MacroSignal | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedStock, setSelectedStock] = useState<Holding | null>(null);
  const [showStockModal, setShowStockModal] = useState(false);

  const [briefingLoading, setBriefingLoading] = useState(true);

  useEffect(() => {
    loadPortfolioData();
    loadMacroSignals();
    loadRiskAlerts();
    loadOpportunities();
    loadDailyBriefing();
  }, []);

  const loadPortfolioData = async () => {
    try {
      setLoading(true);
      const data = await mockAPI.fetchPortfolio();
      setHoldings(data.holdings);
      setProfile(data.profile);
    } catch (error) {
      console.error('Failed to load portfolio data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMacroSignals = async () => {
    try {
      const user = authService.getUser();
      const userId = user?.uid || 'user123';
      const signalData = await mockAPI.fetchMacroSignals(userId);
      setSignals(signalData);
    } catch (error) {
      console.error('Failed to load macro signals:', error);
    }
  };

  const loadRiskAlerts = async () => {
    try {
      const user = authService.getUser();
      const userId = user?.uid || 'user123';
      const alertData = await mockAPI.fetchRiskAlerts(userId);
      setRiskAlerts(alertData);
    } catch (error) {
      console.error('Failed to load risk alerts:', error);
    }
  };

  const loadOpportunities = async () => {
    try {
      const user = authService.getUser();
      const userId = user?.uid || 'user123';
      const oppData = await mockAPI.fetchOpportunities(userId);
      setOpportunities(oppData);
    } catch (error) {
      console.error('Failed to load opportunities:', error);
    }
  };

  const loadDailyBriefing = async () => {
    try {
      setBriefingLoading(true);
      const user = authService.getUser();
      const userId = user?.uid || 'user123';
      
      // Try to get real briefing from backend
      try {
        const briefingData = await insightsService.getDailyBriefing(userId);
        setDailyBriefing(briefingData);
      } catch (error) {
        console.error('Failed to load real briefing, falling back to mock:', error);
        // Fallback to mock data
        const briefingData = await mockAPI.fetchDailyBriefing(userId);
        setDailyBriefing(briefingData);
      }
    } catch (error) {
      console.error('Failed to load daily briefing:', error);
    } finally {
      setBriefingLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await mockAPI.refreshPortfolio();
      await Promise.all([
        loadPortfolioData(), 
        loadMacroSignals(), 
        loadRiskAlerts(), 
        loadOpportunities(),
        loadDailyBriefing()
      ]);
    } catch (error) {
      console.error('Failed to refresh portfolio:', error);
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, var(--obsidian-black) 0%, #1a1d23 100%)' }}>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--cyber-lime)] mx-auto mb-4"></div>
            <p className="text-[var(--platinum-silver)]">Loading your portfolio...</p>
          </div>
        </div>
      </div>
    );
  }

  // Calculate portfolio value from actual holdings
  const totalValue = holdings.reduce((sum, holding) => sum + holding.marketValue, 0);
  const dayChange = holdings.reduce((sum, holding) => sum + holding.dayChange, 0);
  const riskScore = profile?.riskScore || 0;
  


  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, var(--obsidian-black) 0%, #1a1d23 100%)' }}>
      <DashboardTopBar />
      <div className="h-8"></div> {/* Spacer for sticky top bar */}
      <div className="container mx-auto px-6 py-8 pt-8">
        {/* Tab Navigation - Moved to top */}
        <div className="mb-8">
          <div className="flex space-x-1 bg-slate-800 rounded-lg p-1 border border-slate-700">
            <button
              onClick={() => setActiveTab('portfolio')}
              className={`flex-1 py-3 px-6 rounded-md font-medium text-sm transition-all duration-200 ${
                activeTab === 'portfolio'
                  ? 'bg-[var(--cyber-lime)] text-slate-900 shadow-sm'
                  : 'text-slate-300 hover:text-white hover:bg-slate-700'
              }`}
            >
              Portfolio
            </button>
            <button
              onClick={() => setActiveTab('briefing')}
              className={`flex-1 py-3 px-6 rounded-md font-medium text-sm transition-all duration-200 ${
                activeTab === 'briefing'
                  ? 'bg-[var(--cyber-lime)] text-slate-900 shadow-sm'
                  : 'text-slate-300 hover:text-white hover:bg-slate-700'
              }`}
            >
              Daily Briefing
            </button>
            <button
              onClick={() => setActiveTab('alerts')}
              className={`flex-1 py-3 px-6 rounded-md font-medium text-sm transition-all duration-200 ${
                activeTab === 'alerts'
                  ? 'bg-[var(--cyber-lime)] text-slate-900 shadow-sm'
                  : 'text-slate-300 hover:text-white hover:bg-slate-700'
              }`}
            >
              Alerts
            </button>
            <button
              onClick={() => setActiveTab('news')}
              className={`flex-1 py-3 px-6 rounded-md font-medium text-sm transition-all duration-200 ${
                activeTab === 'news'
                  ? 'bg-[var(--cyber-lime)] text-slate-900 shadow-sm'
                  : 'text-slate-300 hover:text-white hover:bg-slate-700'
              }`}
            >
              News
            </button>
          </div>
        </div>

        {/* Portfolio Overview - Only show on Portfolio tab */}
        {activeTab === 'portfolio' && (
          <div className="mb-8">
            <CombinedPortfolioOverview 
              totalValue={totalValue}
              dayChange={dayChange}
              riskScore={riskScore}
              onRefresh={handleRefresh}
              isRefreshing={refreshing}
            />
          </div>
        )}

        {/* Tab Content */}
        {activeTab === 'portfolio' && (
          <div className="mt-6">
            <div className="space-y-4">
              {/* Portfolio Summary - Full Width */}
              <div className="bg-slate-800 rounded-xl p-8 border border-slate-700 shadow-sm">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-white">Portfolio Overview</h2>
                  <div className="flex items-center space-x-2">
                    <PieChartIcon className="w-6 h-6 text-[var(--cyber-lime)]" />
                    <span className="text-sm text-slate-400">Allocation and Exposure</span>
                  </div>
                </div>
                
                {profile && (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <ProfileSummary profile={profile} />
                    <AssetAllocationChart 
                      allocation={profile.assetAllocation}
                    />
                    <SectorExposureChart sectorExposure={profile.sectorExposure} />
                  </div>
                )}
              </div>

              {/* Holdings - Full Width */}
              <div className="bg-slate-800 rounded-xl p-8 border border-slate-700 shadow-sm">
                <h2 className="text-2xl font-bold text-white mb-6">Holdings</h2>
                <HoldingsTable 
                  holdings={holdings} 
                  onStockClick={(holding) => {
                    setSelectedStock(holding);
                    setShowStockModal(true);
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'briefing' && (
          <div className="mt-6">
            <DailyBriefingPanel 
              briefing={dailyBriefing} 
              holdings={holdings}
              profile={profile}
              signals={signals}
              riskAlerts={riskAlerts}
              opportunities={opportunities}
              loading={briefingLoading} 
            />
          </div>
        )}

        {activeTab === 'alerts' && (
          <div className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column - Risk Alerts */}
              <div>
                <RiskAlertsPanel alerts={riskAlerts} />
              </div>

              {/* Right Column - Opportunities */}
              <div>
                <OpportunitiesPanel opportunities={opportunities} />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'news' && (
          <div className="mt-6">
            <div className="space-y-6">
              {/* News Panel - Full Width */}
              <div>
                <NewsPanel signals={signals} onSignalClick={(signal) => {
                  setSelectedSignal(signal);
                  setShowModal(true);
                }} />
              </div>

              {/* Macro Signals Panel - Full Width */}
              <div>
                <MacroSignalsPanel signals={signals} onSignalClick={(signal) => {
                  setSelectedSignal(signal);
                  setShowModal(true);
                }} />
              </div>
            </div>
          </div>
        )}

      {/* AI Summary Modal */}
      {showModal && selectedSignal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          onClick={() => setShowModal(false)}
        >
          <div 
            className="bg-slate-800 rounded-xl p-8 border border-slate-700 shadow-lg max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto mt-16"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">AI Portfolio Impact Analysis</h2>
              <button 
                onClick={() => setShowModal(false)}
                className="text-slate-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Original Signal */}
              <div className="border border-slate-600 rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-white font-semibold text-lg">{selectedSignal.headline}</h3>
                  <button 
                    onClick={() => window.open(selectedSignal.sourceUrl, '_blank')}
                    className="text-slate-400 hover:text-[var(--cyber-lime)] transition-colors flex items-center space-x-1"
                  >
                    <span className="text-xs">Read Article</span>
                    <ExternalLink className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-slate-300 text-sm mb-3">{selectedSignal.summary}</p>
                <div className="flex items-center space-x-4 text-xs">
                  <span className="text-slate-400">{selectedSignal.source}</span>
                  <span className={`px-2 py-1 rounded ${
                    getUrgencyColor(selectedSignal.urgencyLevel)
                  }`}>
                    {selectedSignal.urgencyLevel}
                  </span>
                  <span className="text-slate-400">
                    {getTimeAgo(selectedSignal.publishedAt)}
                  </span>
                </div>
              </div>

              {/* AI Analysis */}
              <div className="space-y-4">
                <h4 className="text-white font-semibold text-lg">Portfolio Impact Analysis</h4>
                
                <div className="bg-slate-700 rounded-lg p-4">
                  <h5 className="text-[var(--cyber-lime)] font-semibold mb-2">Direct Impact</h5>
                  <p className="text-slate-300 text-sm leading-relaxed">
                    This {selectedSignal.urgencyLevel} urgency signal directly affects your portfolio through {selectedSignal.sectorTags.join(', ')} sector exposure. 
                    Your current holdings in these sectors may experience increased volatility or price movements.
                  </p>
                </div>

                <div className="bg-slate-700 rounded-lg p-4">
                  <h5 className="text-[var(--cyber-lime)] font-semibold mb-2">Risk Assessment</h5>
                  <p className="text-slate-300 text-sm leading-relaxed">
                    Based on your portfolio composition, this news has a {Math.round(selectedSignal.relevanceScore * 100)}% relevance score. 
                    The {selectedSignal.timeHorizon} impact timeline suggests {selectedSignal.timeHorizon === 'immediate' ? 'immediate action may be required' : 
                    selectedSignal.timeHorizon === 'short-term' ? 'short-term monitoring is advised' : 'long-term strategic consideration'}.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stock Analysis Modal */}
      {showStockModal && selectedStock && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          onClick={() => setShowStockModal(false)}
        >
          <div 
            className="bg-slate-800 rounded-xl p-8 border border-slate-700 shadow-lg max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto mt-16"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">AI Stock Analysis</h2>
              <button 
                onClick={() => setShowStockModal(false)}
                className="text-slate-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Stock Overview */}
              <div className="border border-slate-600 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-white font-semibold text-lg">{selectedStock.ticker} - {selectedStock.name}</h3>
                  <span className="text-slate-400 text-sm">{selectedStock.sector}</span>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-400">Market Value:</span>
                    <span className="text-white ml-2">{formatCurrency(selectedStock.marketValue)}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Portfolio Weight:</span>
                    <span className="text-white ml-2">{(selectedStock.weight * 100).toFixed(1)}%</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Today's Change:</span>
                    <span className={`ml-2 font-medium ${
                      selectedStock.dayChange >= 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {formatCurrency(selectedStock.dayChange)} ({formatPercent(selectedStock.dayChangePercent)})
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400">Quantity:</span>
                    <span className="text-white ml-2">{selectedStock.quantity.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* AI Analysis */}
              <div className="space-y-4">
                <h4 className="text-white font-semibold text-lg">AI Portfolio Analysis</h4>
                
                <div className="bg-slate-700 rounded-lg p-4">
                  <h5 className="text-[var(--cyber-lime)] font-semibold mb-2">Position Analysis</h5>
                  <p className="text-slate-300 text-sm leading-relaxed">
                    {selectedStock.ticker} represents {(selectedStock.weight * 100).toFixed(1)}% of your portfolio, making it your {selectedStock.weight > 0.15 ? 'largest' : selectedStock.weight > 0.08 ? 'significant' : 'moderate'} holding. 
                    As a {selectedStock.sector} sector stock, it contributes to your overall sector diversification strategy.
                  </p>
                </div>

                <div className="bg-slate-700 rounded-lg p-4">
                  <h5 className="text-[var(--cyber-lime)] font-semibold mb-2">Risk Assessment</h5>
                  <p className="text-slate-300 text-sm leading-relaxed">
                    {selectedStock.ticker} has shown {selectedStock.dayChangePercent >= 0 ? 'positive' : 'negative'} performance today with a {Math.abs(selectedStock.dayChangePercent).toFixed(2)}% change. 
                    Given its {selectedStock.weight > 0.15 ? 'high' : selectedStock.weight > 0.08 ? 'moderate' : 'low'} portfolio weight, 
                    {selectedStock.weight > 0.15 ? ' significant price movements could have a major impact on your overall portfolio performance.' : 
                     selectedStock.weight > 0.08 ? ' price movements will have a noticeable impact on your portfolio.' : 
                     ' price movements will have a limited impact on your overall portfolio.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      </div>
    </div>
  );
};

// Daily Briefing Panel Component (segmented boxes)
const DailyBriefingPanel: React.FC<{
  briefing: DailyBriefing | null;
  holdings: Holding[];
  profile: InvestorProfile | null;
  signals: MacroSignal[];
  riskAlerts: RiskAlert[];
  opportunities: OpportunityInsight[];
  loading: boolean;
}> = ({ briefing, holdings, profile, signals, riskAlerts, opportunities, loading }) => {
  const formatCurrencyLocal = (value: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
  if (loading) {
    return (
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 shadow-sm">
        <div className="flex items-center mb-4">
          <Lightbulb className="w-6 h-6 text-[var(--cyber-lime)] mr-3" />
          <h3 className="text-xl font-bold text-white">Daily Briefing</h3>
        </div>
        <div className="animate-pulse">
          <div className="h-4 bg-slate-600 rounded mb-2"></div>
          <div className="h-4 bg-slate-600 rounded mb-2 w-3/4"></div>
          <div className="h-4 bg-slate-600 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (!briefing) {
    return (
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 shadow-sm">
        <div className="flex items-center mb-4">
          <Lightbulb className="w-6 h-6 text-[var(--cyber-lime)] mr-3" />
          <h3 className="text-xl font-bold text-white">Daily Briefing</h3>
        </div>
        <p className="text-slate-400">No briefing available</p>
      </div>
    );
  }

  // Derived context from current portfolio data
  const sortedByWeight = [...holdings].sort((a, b) => b.weight - a.weight);
  const largest = sortedByWeight[0];
  const sectorExposure = profile?.sectorExposure || {};
  const techExposure = sectorExposure['Technology'] || 0;
  const healthcareExposure = sectorExposure['Healthcare'] || 0;
  const financialsExposure = sectorExposure['Financial Services'] || sectorExposure['Financials'] || 0;
  const discretionaryExposure = sectorExposure['Consumer Discretionary'] || 0;
  const totalDayChange = holdings.reduce((sum, h) => sum + (h.dayChange || 0), 0);

  const growthTilt = techExposure >= 55 ? 'High' : techExposure >= 35 ? 'Mod-High' : techExposure >= 20 ? 'Moderate' : 'Low';
  const momentumTilt = totalDayChange >= 0 ? (totalDayChange > 500 ? 'Mod-High' : 'Moderate') : 'Low';
  const defensiveTilt = (healthcareExposure) < 12 ? 'Low' : 'Moderate';

  const highConfidenceOpps = opportunities.filter(o => o.confidence >= 0.7).length;
  const opportunityRating = highConfidenceOpps > 0 ? 6.0 + highConfidenceOpps * 0.2 : 5.4; // simple heuristic scale

  // Simple event matrix using available context
  const eventMatrix = [
    {
      event: 'Fed Policy Update',
      why: 'Tech exposure is sensitive to rate expectations',
      reaction: techExposure > 40 ? 'Tech-led swings; defensives offset' : 'Broad market drift',
      confidence: 'High',
      prob: '60%'
    },
    ...(holdings.some(h => h.ticker === 'JPM') ? [{
      event: 'Bank Earnings (JPM)',
      why: 'Tone-setter for financials, correlation with payments',
      reaction: holdings.some(h => h.ticker === 'V') ? 'JPM → V correlation likely' : 'Financials set sector tone',
      confidence: 'Medium',
      prob: '70%'
    }] : []),
  ];

  const watchFlags = [
    largest ? { color: 'bg-red-500/20 text-red-300', label: `High Attention: ${largest.ticker} (largest weight)` } : null,
    holdings.some(h => h.ticker === 'JPM') && holdings.some(h => h.ticker === 'V')
      ? { color: 'bg-yellow-500/20 text-yellow-300', label: 'Watch: JPM → V correlation on earnings' }
      : null,
    (holdings.some(h => h.ticker === 'JNJ') || holdings.some(h => h.ticker === 'UNH'))
      ? { color: 'bg-green-500/20 text-green-300', label: 'Stabilizer: Healthcare holdings can buffer tech swings' }
      : null,
  ].filter(Boolean) as { color: string; label: string }[];

  return (
    <div className="space-y-6">
      {/* Header / Theme */}
    <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 shadow-sm">
        <div className="flex items-center justify-between mb-2">
        <div className="flex items-center">
          <Lightbulb className="w-6 h-6 text-[var(--cyber-lime)] mr-3" />
            <h3 className="text-xl font-bold text-white">Briqq Daily Brief</h3>
        </div>
          <span className="text-sm text-slate-400">{new Date(briefing.generatedAt).toLocaleDateString()}</span>
        </div>
        <div className="text-slate-300 text-sm">
          <div className="text-white font-semibold text-lg">{briefing.headline}</div>
          <div className="mt-2"><span className="text-slate-400 font-medium">Key Takeaway: </span>{briefing.summary}</div>
        </div>
      </div>

      {/* Since Yesterday */}
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 shadow-sm">
        <h4 className="text-white font-semibold mb-4">Since Yesterday</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-lg border border-slate-700 p-4">
            <div className="text-slate-400 text-xs mb-1">Overall Risk Score</div>
            <div className="text-white text-lg font-semibold">{profile?.riskScore ?? 0}</div>
            <div className="text-slate-400 text-xs">Concentration and beta driven</div>
          </div>
          <div className="rounded-lg border border-slate-700 p-4">
            <div className="text-slate-400 text-xs mb-1">Opportunity Rating</div>
            <div className="text-white text-lg font-semibold">{opportunityRating.toFixed(1)}</div>
            <div className="text-slate-400 text-xs">{highConfidenceOpps} high-confidence idea{highConfidenceOpps === 1 ? '' : 's'}</div>
          </div>
          <div className="rounded-lg border border-slate-700 p-4">
            <div className="text-slate-400 text-xs mb-1">Analyst/Signals</div>
            <div className="text-white text-lg font-semibold">{signals.length}</div>
            <div className="text-slate-400 text-xs">Relevant macro/company signals</div>
          </div>
        </div>
      </div>

      {/* Market & Sector Context */}
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 shadow-sm">
        <h4 className="text-white font-semibold mb-4">Market & Sector Context</h4>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Sector Exposure Overview */}
          <div>
            <h5 className="text-white font-medium mb-3">Sector Exposure</h5>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <span className="text-slate-300 text-sm">Technology</span>
                </div>
                <div className="text-right">
                  <div className="text-white font-semibold">{techExposure.toFixed(0)}%</div>
                  <div className="text-slate-400 text-xs">Rate-sensitive</div>
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-slate-300 text-sm">Healthcare</span>
                </div>
                <div className="text-right">
                  <div className="text-white font-semibold">{healthcareExposure.toFixed(0)}%</div>
                  <div className="text-slate-400 text-xs">Defensive buffer</div>
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <span className="text-slate-300 text-sm">Financials</span>
                </div>
                <div className="text-right">
                  <div className="text-white font-semibold">{financialsExposure.toFixed(0)}%</div>
                  <div className="text-slate-400 text-xs">Bank correlation</div>
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                  <span className="text-slate-300 text-sm">Consumer Discretionary</span>
                </div>
                <div className="text-right">
                  <div className="text-white font-semibold">{discretionaryExposure.toFixed(0)}%</div>
                  <div className="text-slate-400 text-xs">Macro sensitive</div>
                </div>
              </div>
            </div>
          </div>

          {/* Market Context */}
          <div>
            <h5 className="text-white font-medium mb-3">Market Context</h5>
            <div className="space-y-3">
              <div className="p-3 bg-slate-700/50 rounded-lg">
                <div className="text-slate-400 text-xs mb-1">Portfolio Beta</div>
                <div className="text-white font-semibold">{profile?.portfolioBeta?.toFixed(2) || '1.12'}</div>
                <div className="text-slate-400 text-xs mt-1">Market correlation indicator</div>
              </div>
              <div className="p-3 bg-slate-700/50 rounded-lg">
                <div className="text-slate-400 text-xs mb-1">Concentration Risk</div>
                <div className={`font-semibold ${
                  profile?.concentrationRisk === 'high' || profile?.concentrationRisk === 'critical' 
                    ? 'text-red-400' : 'text-yellow-400'
                }`}>
                  {profile?.concentrationRisk ? profile.concentrationRisk.charAt(0).toUpperCase() + profile.concentrationRisk.slice(1) : 'Medium'}
                </div>
                <div className="text-slate-400 text-xs mt-1">Portfolio diversification level</div>
              </div>
              <div className="p-3 bg-slate-700/50 rounded-lg">
                <div className="text-slate-400 text-xs mb-1">Risk Alerts</div>
                <div className="text-white font-semibold">{riskAlerts.length}</div>
                <div className="text-slate-400 text-xs mt-1">{riskAlerts.length > 0 ? 'Active risk alerts detected' : 'No active risk alerts'}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Portfolio Risk & Sensitivity Analysis */}
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 shadow-sm">
        <h4 className="text-white font-semibold mb-4">Portfolio Risk & Sensitivity Analysis</h4>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Factor Exposure */}
          <div>
            <h5 className="text-white font-medium mb-3">Factor & Style Exposure</h5>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              <div className="rounded-lg border border-slate-700 p-3">
                <div className="text-slate-400 text-xs">Growth Tilt</div>
                <div className="text-white font-semibold">{growthTilt}</div>
                <div className="text-slate-400 text-xs">Tech exposure: {techExposure.toFixed(0)}%</div>
              </div>
              <div className="rounded-lg border border-slate-700 p-3">
                <div className="text-slate-400 text-xs">Momentum</div>
                <div className="text-white font-semibold">{momentumTilt}</div>
                <div className="text-slate-400 text-xs">Day change: {formatCurrencyLocal(totalDayChange)}</div>
              </div>
              <div className="rounded-lg border border-slate-700 p-3">
                <div className="text-slate-400 text-xs">Defensive</div>
                <div className="text-white font-semibold">{defensiveTilt}</div>
                <div className="text-slate-400 text-xs">Healthcare: {healthcareExposure.toFixed(0)}%</div>
              </div>
            </div>
          </div>

          {/* Event Sensitivity */}
          <div>
            <h5 className="text-white font-medium mb-3">Key Event Sensitivity</h5>
            <div className="space-y-2">
              {eventMatrix.slice(0, 2).map((e, idx) => (
                <div key={idx} className="rounded-lg border border-slate-700 p-3">
                  <div className="text-white font-medium text-sm">{e.event}</div>
                  <div className="text-slate-300 text-xs mt-1">{e.why}</div>
                  <div className="text-slate-400 text-xs mt-1">Reaction: {e.reaction}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Portfolio Watch & Alerts */}
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 shadow-sm">
        <h4 className="text-white font-semibold mb-4">Portfolio Watch & Alerts</h4>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Watch Flags */}
          <div>
            <h5 className="text-white font-medium mb-3">Today's Watch Flags</h5>
            <div className="space-y-2">
              {watchFlags.map((f, i) => (
                <div key={i} className={`rounded px-3 py-2 ${f.color} border border-slate-700/50 text-sm`}>{f.label}</div>
              ))}
              {watchFlags.length === 0 && <div className="text-slate-400 text-sm">No special flags today.</div>}
            </div>
          </div>

          {/* Key Holdings Focus */}
          <div>
            <h5 className="text-white font-medium mb-3">Key Holdings Focus</h5>
            {largest && (
              <div className="bg-slate-700/50 rounded-lg p-3">
                <div className="text-white font-medium text-sm">{largest.ticker} - {(largest.weight * 100).toFixed(0)}% weight</div>
                <div className="text-slate-300 text-xs mt-1">Sector: {largest.sector}</div>
                <div className="text-slate-400 text-xs mt-1">Monitor for portfolio-wide impact</div>
              </div>
            )}
            <div className="mt-2 text-slate-400 text-xs">
              Tech concentration: {techExposure.toFixed(0)}% • Healthcare: {healthcareExposure.toFixed(0)}%
            </div>
          </div>
        </div>
      </div>

      {/* Analyst Coverage & Ratings */}
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 shadow-sm">
        <h4 className="text-white font-semibold mb-4">Analyst Coverage & Ratings</h4>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Top Rated Holdings */}
          <div className="bg-slate-700/50 rounded-lg p-4">
            <h5 className="text-white font-medium mb-3">Top Rated Holdings</h5>
            <div className="space-y-3">
              {holdings.slice(0, 4).map((holding, index) => {
                const rating = ['Buy', 'Buy', 'Strong Buy', 'Buy'][index % 4];
                const currentPrice = holding.quantity > 0 ? holding.marketValue / holding.quantity : 0;
                const priceTarget = currentPrice * (1 + (0.08 + index * 0.02));
                const analystCount = 12 + index * 3;
                return (
                  <div key={index} className="flex justify-between items-center">
                    <div>
                      <span className="text-white font-medium text-sm">{holding.ticker}</span>
                      <div className="text-slate-400 text-xs">{holding.sector}</div>
                    </div>
                    <div className="text-right">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        rating === 'Strong Buy' ? 'bg-green-500/20 text-green-300' :
                        rating === 'Buy' ? 'bg-blue-500/20 text-blue-300' :
                        'bg-yellow-500/20 text-yellow-300'
                      }`}>
                        {rating}
        </span>
                      <div className="text-slate-300 text-xs mt-1">${priceTarget.toFixed(2)}</div>
                      <div className="text-slate-400 text-xs">{analystCount} analysts</div>
                    </div>
                  </div>
                );
              })}
            </div>
      </div>
      
          {/* Analyst Consensus */}
          <div className="bg-slate-700/50 rounded-lg p-4">
            <h5 className="text-white font-medium mb-3">Portfolio Consensus</h5>
            <div className="space-y-4">
        <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-slate-300 text-sm">Buy Ratings</span>
                  <span className="text-white font-medium">72%</span>
                </div>
                <div className="w-full bg-slate-600 rounded-full h-2">
                  <div className="bg-green-500 h-2 rounded-full" style={{ width: '72%' }}></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-slate-300 text-sm">Hold Ratings</span>
                  <span className="text-white font-medium">22%</span>
                </div>
                <div className="w-full bg-slate-600 rounded-full h-2">
                  <div className="bg-yellow-500 h-2 rounded-full" style={{ width: '22%' }}></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-slate-300 text-sm">Sell Ratings</span>
                  <span className="text-white font-medium">6%</span>
                </div>
                <div className="w-full bg-slate-600 rounded-full h-2">
                  <div className="bg-red-500 h-2 rounded-full" style={{ width: '6%' }}></div>
                </div>
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-600">
              <div className="text-slate-300 text-sm">Avg. Price Target</div>
              <div className="text-white font-medium">+18.4%</div>
            </div>
        </div>
        
          {/* Recent Analyst Actions */}
          <div className="bg-slate-700/50 rounded-lg p-4">
            <h5 className="text-white font-medium mb-3">Recent Analyst Actions</h5>
            <div className="space-y-3">
              {[
                { firm: 'Goldman Sachs', action: 'Upgraded AAPL', rating: 'Buy' },
                { firm: 'Morgan Stanley', action: 'Raised target MSFT', rating: 'Overweight' },
                { firm: 'JPMorgan', action: 'Initiated coverage META', rating: 'Overweight' },
                { firm: 'Bank of America', action: 'Reiterated TSLA', rating: 'Buy' }
              ].map((item, index) => (
                <div key={index} className="border-b border-slate-600 pb-2 last:border-b-0">
                  <div className="text-white text-sm font-medium">{item.firm}</div>
                  <div className="text-slate-300 text-xs">{item.action}</div>
                  <div className="flex items-center mt-1">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      item.rating.includes('Buy') || item.rating.includes('Overweight') ? 'bg-green-500/20 text-green-300' :
                      'bg-yellow-500/20 text-yellow-300'
                    }`}>
                      {item.rating}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Action Items & Key Insights (from backend brief) */}
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 shadow-sm">
        <h4 className="text-white font-semibold mb-4">Key Insights & Actions</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {briefing.keyInsights.length > 0 && (
          <div>
              <div className="text-white font-medium mb-3">Key Insights</div>
            <ul className="space-y-2">
              {briefing.keyInsights.map((insight, index) => (
                <li key={index} className="text-slate-300 text-sm flex items-start">
                  <span className="text-[var(--cyber-lime)] mr-3 mt-1">•</span>
                  <span className="leading-relaxed">{insight}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {briefing.actionableItems.length > 0 && (
          <div>
              <div className="text-white font-medium mb-3">Action Items</div>
            <ul className="space-y-2">
              {briefing.actionableItems.map((item, index) => (
                <li key={index} className="text-slate-300 text-sm flex items-start">
                  <span className="text-[var(--cyber-lime)] mr-3 mt-1">→</span>
                  <span className="leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

// Risk Alerts Panel Component
const RiskAlertsPanel: React.FC<{ alerts: RiskAlert[] }> = ({ alerts }) => {
  return (
    <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 shadow-sm">
      <div className="flex items-center mb-6">
        <AlertTriangle className="w-6 h-6 text-[var(--signal-red)] mr-3" />
        <h3 className="text-xl font-bold text-white">Risk Alerts</h3>
        {alerts.length > 0 && (
          <span className="ml-auto bg-[var(--signal-red)] text-white text-xs px-3 py-1 rounded-full font-medium">
            {alerts.length}
          </span>
        )}
      </div>
      
      {alerts.length === 0 ? (
        <p className="text-slate-400 text-sm">No risk alerts at this time</p>
      ) : (
        <div className="space-y-4">
          {alerts.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()).map(alert => (
            <div key={alert.id} className="border-l-4 border-[var(--signal-red)] pl-4 py-3 bg-red-900/20 rounded-r-lg">
              <h4 className="text-white font-semibold text-sm mb-2">{alert.title}</h4>
              <p className="text-slate-300 text-xs mb-3 leading-relaxed">{alert.description}</p>
              <div className="flex items-center text-xs mb-3">
                <span className={`px-3 py-1 rounded-full font-medium ${
                  alert.severity === 'critical' ? 'bg-[var(--signal-red)] text-white' : 
                  alert.severity === 'high' ? 'bg-orange-500 text-white' :
                  alert.severity === 'medium' ? 'bg-yellow-500 text-black' :
                  'bg-green-500 text-white'
                }`}>
                  {alert.severity}
                </span>
                <span className="text-slate-400 ml-3">
                  {getTimeAgo(alert.createdAt)}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xs text-slate-400">Affected:</span>
                {alert.affectedPositions.map((position, index) => (
                  <span 
                    key={index} 
                    className="text-xs bg-slate-700 text-slate-300 px-2 py-1 rounded"
                  >
                    {position}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Opportunities Panel Component
const OpportunitiesPanel: React.FC<{ opportunities: OpportunityInsight[] }> = ({ opportunities }) => {
  return (
    <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 shadow-sm">
      <div className="flex items-center mb-6">
        <TrendingUp className="w-6 h-6 text-[var(--cyber-lime)] mr-3" />
        <h3 className="text-xl font-bold text-white">Opportunities</h3>
        {opportunities.length > 0 && (
          <span className="ml-auto bg-[var(--cyber-lime)] text-[var(--obsidian-black)] text-xs px-3 py-1 rounded-full font-medium">
            {opportunities.length}
          </span>
        )}
      </div>
      
      {opportunities.length === 0 ? (
        <p className="text-slate-400 text-sm">No opportunities identified</p>
      ) : (
        <div className="space-y-4">
          {opportunities.map(opportunity => (
            <div key={opportunity.id} className="border-l-4 border-[var(--cyber-lime)] pl-4 py-3 bg-green-900/20 rounded-r-lg">
              <h4 className="text-white font-semibold text-sm mb-2">{opportunity.title}</h4>
              <p className="text-slate-300 text-xs mb-3 leading-relaxed">{opportunity.description}</p>
              <div className="flex items-center justify-between">
                <span className="text-[var(--cyber-lime)] text-xs font-semibold">
                  +{opportunity.potentialBenefit.safetyScoreImprovement} Safety Score
                </span>
                <span className="text-slate-400 text-xs">
                  {opportunity.timeframe}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// News Panel Component
const NewsPanel: React.FC<{ signals: MacroSignal[]; onSignalClick: (signal: MacroSignal) => void }> = ({ signals, onSignalClick }) => {
  const newsSignals = signals.filter(signal => 
    (signal.source.includes('Reuters') || 
    signal.source.includes('Bloomberg') || 
    signal.source.includes('CNBC') ||
    signal.source.includes('Financial Times')) &&
    !signal.headline.toLowerCase().includes('fed') &&
    !signal.headline.toLowerCase().includes('inflation') &&
    !signal.headline.toLowerCase().includes('rate') &&
    !signal.headline.toLowerCase().includes('cpi') &&
    !signal.headline.toLowerCase().includes('earnings') &&
    !signal.headline.toLowerCase().includes('volatility')
  );

  return (
    <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 shadow-sm">
      <div className="flex items-center mb-6">
        <BarChart3 className="w-6 h-6 text-[var(--cyber-lime)] mr-3" />
        <h3 className="text-xl font-bold text-white">Relevant News</h3>
        {newsSignals.length > 0 && (
          <span className="ml-auto bg-[var(--cyber-lime)] text-[var(--obsidian-black)] text-xs px-3 py-1 rounded-full font-medium">
            {newsSignals.length}
          </span>
        )}
      </div>
      
      {newsSignals.length === 0 ? (
        <p className="text-slate-400 text-sm">No news available</p>
      ) : (
        <div className="space-y-4">
          {newsSignals.slice(0, 5).map(signal => (
            <div 
              key={signal.id} 
              className="border border-slate-600 rounded-lg p-4 hover:border-[var(--cyber-lime)] hover:shadow-md transition-all duration-200 cursor-pointer"
              onClick={() => onSignalClick(signal)}
            >
              <h4 className="text-white font-semibold text-base mb-3">{signal.headline}</h4>
              <p className="text-slate-300 text-sm mb-3 leading-relaxed">{signal.summary}</p>
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-400">{signal.source}</span>
                <span className={`px-2 py-1 rounded ${
                  getUrgencyColor(signal.urgencyLevel)
                }`}>
                  {signal.urgencyLevel}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Macro Signals Panel Component
const MacroSignalsPanel: React.FC<{ signals: MacroSignal[]; onSignalClick: (signal: MacroSignal) => void }> = ({ signals, onSignalClick }) => {
  const macroSignals = signals.filter(signal => 
    (!signal.source.includes('Reuters') && 
    !signal.source.includes('Bloomberg') && 
    !signal.source.includes('CNBC') &&
    !signal.source.includes('Financial Times')) ||
    signal.headline.toLowerCase().includes('fed') ||
    signal.headline.toLowerCase().includes('inflation') ||
    signal.headline.toLowerCase().includes('rate') ||
    signal.headline.toLowerCase().includes('cpi') ||
    signal.headline.toLowerCase().includes('earnings') ||
    signal.headline.toLowerCase().includes('volatility')
  );

  return (
    <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 shadow-sm">
      <div className="flex items-center mb-6">
        <BarChart3 className="w-6 h-6 text-[var(--cyber-lime)] mr-3" />
        <h3 className="text-xl font-bold text-white">Macro Signals</h3>
        {macroSignals.length > 0 && (
          <span className="ml-auto bg-[var(--cyber-lime)] text-[var(--obsidian-black)] text-xs px-3 py-1 rounded-full font-medium">
            {macroSignals.length}
          </span>
        )}
      </div>
      
      {macroSignals.length === 0 ? (
        <p className="text-slate-400 text-sm">No macro signals available</p>
      ) : (
        <div className="space-y-4">
          {macroSignals.slice(0, 5).map(signal => (
            <div 
              key={signal.id} 
              className="border border-slate-600 rounded-lg p-4 hover:border-[var(--cyber-lime)] hover:shadow-md transition-all duration-200 cursor-pointer"
              onClick={() => onSignalClick(signal)}
            >
              <h4 className="text-white font-semibold text-base mb-3">{signal.headline}</h4>
              <p className="text-slate-300 text-sm mb-3 leading-relaxed">{signal.summary}</p>
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-400">{signal.source}</span>
                <span className={`px-2 py-1 rounded ${
                  getUrgencyColor(signal.urgencyLevel)
                }`}>
                  {signal.urgencyLevel}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};



// Combined Portfolio Overview Component
const CombinedPortfolioOverview: React.FC<{ 
  totalValue: number; 
  dayChange: number; 
  riskScore: number;
  onRefresh: () => void;
  isRefreshing: boolean;
}> = ({ totalValue, dayChange, riskScore, onRefresh, isRefreshing }) => {
  const [selectedPeriod, setSelectedPeriod] = useState<string>('ALL');



  // Generate realistic portfolio data based on actual holdings
  const generatePortfolioData = (period: string): DataPoint[] => {
    const now = new Date();
    let days = 0;

    switch (period) {
      case '1D':
        days = 1;
        break;
      case '1W':
        days = 7;
        break;
      case '1M':
        days = 30;
        break;
      case '3M':
        days = 90;
        break;
      case 'YTD':
        days = Math.floor((now.getTime() - new Date(now.getFullYear(), 0, 1).getTime()) / (1000 * 60 * 60 * 24));
        break;
      case '1Y':
        days = 365;
        break;
      case 'ALL':
      default:
        days = 730; // 2 years
        break;
    }

    // Use a deterministic seed based on period and totalValue
    const seed = period.charCodeAt(0) + Math.floor(totalValue);
    let randomSeed = seed;

    // Simple deterministic random function
    const seededRandom = () => {
      randomSeed = (randomSeed * 9301 + 49297) % 233280;
      return randomSeed / 233280;
    };

    // Generate data in chronological order with smooth progression
    const dataPoints: DataPoint[] = [];
    
    // Calculate a realistic starting value that allows for gradual growth
    // We want the growth to be spread evenly across the time period
    const totalGrowth = totalValue * 0.25; // Assume 25% total growth over the period
    const dailyGrowthRate = Math.pow(1 + (totalGrowth / (totalValue - totalGrowth)), 1 / days) - 1;
    
    // Start from the calculated historical value
    let currentValue = totalValue / Math.pow(1 + dailyGrowthRate, days);
    
    for (let i = 0; i <= days; i++) {
      const date = new Date(now);
      date.setDate(date.getDate() - (days - i));
      
      if (i === 0) {
        // First point (oldest) - use calculated historical value
        currentValue = totalValue / Math.pow(1 + dailyGrowthRate, days);
      } else {
        // Apply consistent daily growth with some realistic volatility
        const volatility = 0.006; // 0.6% daily volatility (very conservative)
        const randomChange = (seededRandom() - 0.5) * volatility;
        
        // Apply the calculated growth rate plus small random movements
        currentValue *= (1 + dailyGrowthRate + randomChange);
      }
      
      // Ensure the last point (most recent) is exactly the current total value
      if (i === days) {
        currentValue = totalValue;
      }
      
      const previousValue = dataPoints.length > 0 ? dataPoints[dataPoints.length - 1].value : currentValue;
      const change = currentValue - previousValue;
      const changePercent = previousValue > 0 ? (change / previousValue) * 100 : 0;

      dataPoints.push({
        date: date.toISOString().split('T')[0],
        value: Math.round(currentValue * 100) / 100,
        change: Math.round(change * 100) / 100,
        changePercent: Math.round(changePercent * 100) / 100,
      });
    }

    return dataPoints;
  };

  const chartData = generatePortfolioData(selectedPeriod);
  const periods = ['1D', '1W', '1M', '3M', 'YTD', '1Y', 'ALL'];

  const totalChange = dayChange;
  const totalChangePercent = totalValue > 0 ? (dayChange / totalValue) * 100 : 0;

  return (
    <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 shadow-sm">
      {/* Header with Portfolio Info and Refresh Button */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-8">
          <div>
            <h1 className="text-3xl font-bold text-white">Portfolio</h1>
            <p className="text-slate-400">Your investment overview</p>
          </div>
          
          <div className="flex items-center space-x-8">
            <div>
              <div className="text-sm text-slate-400">Total Value</div>
              <div className="text-2xl font-bold text-white">
                {formatCurrency(totalValue)}
              </div>
            </div>
            
            <div>
              <div className="text-sm text-slate-400">Today's Change</div>
              <div className={`text-lg font-semibold flex items-center ${
                totalChange >= 0 ? 'text-[var(--cyber-lime)]' : 'text-[var(--signal-red)]'
              }`}>
                {totalChange >= 0 ? (
                  <TrendingUp className="w-5 h-5 mr-2" />
                ) : (
                  <TrendingDown className="w-5 h-5 mr-2" />
                )}
                {formatCurrency(Math.abs(totalChange))} ({formatPercent(totalChangePercent)})
              </div>
            </div>
            

          </div>
        </div>
        
        <div className="text-right">
          <div className="text-sm text-slate-400 mb-2">Safety Score</div>
          <div className="text-2xl font-bold text-white flex items-center justify-end">
            <Shield className={`w-6 h-6 mr-2 ${getRiskColor(riskScore)}`} />
            {riskScore}/100
          </div>
          <div className="text-xs text-red-400 mt-1">-2 since yesterday</div>
          <div className="mt-3">
            <button
              onClick={onRefresh}
              disabled={isRefreshing}
              className={`px-3 py-1 rounded-md text-xs font-medium border border-slate-600 ${
                isRefreshing ? 'text-slate-400' : 'text-slate-200 hover:text-white hover:bg-slate-700'
              }`}
            >
              {isRefreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="mb-6">
        <PortfolioLineChart
          data={chartData}
          height={300}
          color="#00D4AA"
          areaColor="rgba(0, 212, 170, 0.1)"
          strokeWidth={2}
          className="w-full"
        />
      </div>

      {/* Time Period Selectors */}
      <div className="flex gap-4">
        {periods.map((period) => (
          <button
            key={period}
            onClick={() => setSelectedPeriod(period)}
            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
              selectedPeriod === period
                ? 'bg-green-500 text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            {period}
          </button>
        ))}
      </div>
    </div>
  );
};


// Types
interface Holding {
  id: string;
  ticker: string;
  name: string;
  quantity: number;
  marketValue: number;
  weight: number;
  sector: string;
  assetClass: string;
  dayChange: number;
  dayChangePercent: number;
}

interface InvestorProfile {
  userId: string;
  totalPortfolioValue: number;
  riskScore: number;
  portfolioBeta: number;
  concentrationRisk: 'low' | 'medium' | 'high' | 'critical';
  investorArchetype: 'growth' | 'income' | 'balanced' | 'conservative' | 'aggressive';
  riskTolerance: 'low' | 'medium' | 'high';
  assetAllocation: {
    equity: number;
    bond: number;
    alternative: number;
    cash: number;
  };
  sectorExposure: Record<string, number>;
  aiSummary: string;
  profileConfidence: number;
}

interface MacroSignal {
  id: string;
  headline: string;
  summary: string;
  source: string;
  sourceUrl: string;
  publishedAt: Date;
  relevanceScore: number;
  urgencyLevel: 'low' | 'medium' | 'high' | 'critical';
  timeHorizon: 'immediate' | 'short-term' | 'medium-term';
  sectorTags: string[];
  impactMagnitude: number;
  personalizedSummary?: string;
}

interface RiskAlert {
  id: string;
  alertType: 'concentration_risk' | 'sector_overexposure' | 'volatility_spike' | 'correlation_risk';
  severity: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  description: string;
  affectedPositions: string[];
  estimatedImpact: {
    dollarAmount: number;
    percentageOfPortfolio: number;
  };
  createdAt: Date;
}

interface OpportunityInsight {
  id: string;
  opportunityType: 'sector_rotation' | 'momentum_play' | 'value_opportunity' | 'defensive_move';
  confidence: number;
  title: string;
  description: string;
  potentialBenefit: {
    safetyScoreImprovement?: number;
    dollarAmount?: number;
    percentageOfPortfolio: number;
  };
  alignsWithArchetype: boolean;
  timeframe?: string;
}

interface DailyBriefing {
  id: string;
  headline: string;
  summary: string;
  keyInsights: string[];
  riskHighlight: string;
  opportunityHighlight: string;
  actionableItems: string[];
  tone: string;
  generatedAt: string;
  confidence: number;
}

// Mock API
const mockAPI = {
  async fetchPortfolio(): Promise<{ holdings: Holding[], profile: InvestorProfile }> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    return {
      holdings: [
        {
          id: '2',
          ticker: 'MSFT',
          name: 'Microsoft Corporation',
          quantity: 50,
          marketValue: 26782.00,
          weight: 0.188,
          sector: 'Technology',
          assetClass: 'equity',
          dayChange: 576.50,
          dayChangePercent: 2.20
        },
        {
          id: '6',
          ticker: 'JPM',
          name: 'JPMorgan Chase & Co.',
          quantity: 60,
          marketValue: 17655.60,
          weight: 0.124,
          sector: 'Financial Services',
          assetClass: 'equity',
          dayChange: 293.40,
          dayChangePercent: 1.69
        },
        {
          id: '9',
          ticker: 'META',
          name: 'Meta Platforms Inc.',
          quantity: 25,
          marketValue: 19409.25,
          weight: 0.136,
          sector: 'Technology',
          assetClass: 'equity',
          dayChange: 659.00,
          dayChangePercent: 3.51
        },
        {
          id: '1',
          ticker: 'AAPL',
          name: 'Apple Inc.',
          quantity: 100,
          marketValue: 20335.00,
          weight: 0.143,
          sector: 'Technology',
          assetClass: 'equity',
          dayChange: 97.00,
          dayChangePercent: 0.48
        },
        {
          id: '4',
          ticker: 'TSLA',
          name: 'Tesla, Inc.',
          quantity: 40,
          marketValue: 12370.40,
          weight: 0.087,
          sector: 'Consumer Discretionary',
          assetClass: 'equity',
          dayChange: 265.20,
          dayChangePercent: 2.19
        },
        {
          id: '7',
          ticker: 'JNJ',
          name: 'Johnson & Johnson',
          quantity: 45,
          marketValue: 7696.80,
          weight: 0.054,
          sector: 'Healthcare',
          assetClass: 'equity',
          dayChange: 166.95,
          dayChangePercent: 2.22
        },
        {
          id: '8',
          ticker: 'AMZN',
          name: 'Amazon.com Inc.',
          quantity: 35,
          marketValue: 7407.75,
          weight: 0.052,
          sector: 'Consumer Discretionary',
          assetClass: 'equity',
          dayChange: -108.50,
          dayChangePercent: -1.44
        },
        {
          id: '11',
          ticker: 'V',
          name: 'Visa Inc.',
          quantity: 20,
          marketValue: 6843.00,
          weight: 0.048,
          sector: 'Financial Services',
          assetClass: 'equity',
          dayChange: 56.00,
          dayChangePercent: 0.83
        },
        {
          id: '12',
          ticker: 'HD',
          name: 'Home Depot Inc.',
          quantity: 15,
          marketValue: 5712.30,
          weight: 0.040,
          sector: 'Consumer Discretionary',
          assetClass: 'equity',
          dayChange: 107.40,
          dayChangePercent: 1.92
        },
        {
          id: '3',
          ticker: 'GOOGL',
          name: 'Alphabet Inc.',
          quantity: 30,
          marketValue: 5851.20,
          weight: 0.041,
          sector: 'Technology',
          assetClass: 'equity',
          dayChange: 177.30,
          dayChangePercent: 3.12
        },
        {
          id: '5',
          ticker: 'NVDA',
          name: 'NVIDIA Corporation',
          quantity: 25,
          marketValue: 4500.00,
          weight: 0.032,
          sector: 'Technology',
          assetClass: 'equity',
          dayChange: 157.00,
          dayChangePercent: 3.62
        },
        {
          id: '10',
          ticker: 'UNH',
          name: 'UnitedHealth Group Inc.',
          quantity: 15,
          marketValue: 3614.70,
          weight: 0.025,
          sector: 'Healthcare',
          assetClass: 'equity',
          dayChange: 48.15,
          dayChangePercent: 1.35
        }
      ],
      profile: {
        userId: 'user123',
        totalPortfolioValue: 142317.00,
        riskScore: 68,
        portfolioBeta: 1.12,
        concentrationRisk: 'medium',
        investorArchetype: 'growth',
        riskTolerance: 'medium',
        assetAllocation: {
          equity: 85,
          bond: 10,
          alternative: 3,
          cash: 2
        },
        sectorExposure: {
          'Technology': 57,
          'Consumer Discretionary': 18,
          'Healthcare': 10,
          'Financial Services': 10,
          'Other': 5
        },
        aiSummary: 'Growth-oriented portfolio with 57% technology exposure across 5 major tech holdings. Diversified into healthcare, financial services, and consumer discretionary. Moderate risk tolerance with 12 total holdings across 4 sectors.',
        profileConfidence: 0.88
      }
    };
  },

  async refreshPortfolio(): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 500));
  },

  async fetchMacroSignals(userId: string): Promise<MacroSignal[]> {
    void userId;
    await new Promise(resolve => setTimeout(resolve, 800));
    
    return [
      {
        id: '1',
        headline: 'Federal Reserve Signals Potential Rate Cut',
        summary: 'The Fed indicated a possible rate cut in the next quarter, which could boost growth stocks.',
        source: 'Financial Times',
        sourceUrl: '#',
        publishedAt: new Date(Date.now() - 2 * 60 * 60 * 1000),
        relevanceScore: 0.92,
        urgencyLevel: 'high',
        timeHorizon: 'short-term',
        sectorTags: ['Financial Services', 'Technology'],
        impactMagnitude: 0.8
      },
      {
        id: '2',
        headline: 'Tech Earnings Beat Expectations',
        summary: 'Major tech companies reported stronger-than-expected earnings, driving market optimism.',
        source: 'Bloomberg',
        sourceUrl: '#',
        publishedAt: new Date(Date.now() - 4 * 60 * 60 * 1000),
        relevanceScore: 0.88,
        urgencyLevel: 'medium',
        timeHorizon: 'immediate',
        sectorTags: ['Technology'],
        impactMagnitude: 0.7
      },
      {
        id: '3',
        headline: 'Inflation Data Shows Cooling Trend',
        summary: 'Latest inflation figures suggest price pressures are easing, supporting the case for rate cuts.',
        source: 'Reuters',
        sourceUrl: '#',
        publishedAt: new Date(Date.now() - 6 * 60 * 60 * 1000),
        relevanceScore: 0.85,
        urgencyLevel: 'medium',
        timeHorizon: 'medium-term',
        sectorTags: ['Financial Services'],
        impactMagnitude: 0.6
      },
      {
        id: '6',
        headline: 'Tech Earnings Season Kicks Off with Mixed Results',
        summary: 'Major technology companies begin reporting Q4 earnings with varying performance. AAPL and MSFT show strong results while GOOGL faces advertising headwinds.',
        source: 'Financial Times',
        sourceUrl: 'https://ft.com/tech-earnings',
        publishedAt: new Date(Date.now() - 2 * 60 * 60 * 1000),
        relevanceScore: 0.85,
        urgencyLevel: 'medium',
        timeHorizon: 'short-term',
        sectorTags: ['Technology'],
        impactMagnitude: 0.7
      },
      {
        id: '7',
        headline: 'Healthcare Sector Faces Regulatory Uncertainty',
        summary: 'New healthcare regulations could impact JNJ and UNH operations. Investors monitoring potential changes to drug pricing and insurance policies.',
        source: 'Reuters',
        sourceUrl: 'https://reuters.com/healthcare-regulations',
        publishedAt: new Date(Date.now() - 3 * 60 * 60 * 1000),
        relevanceScore: 0.72,
        urgencyLevel: 'medium',
        timeHorizon: 'medium-term',
        sectorTags: ['Healthcare'],
        impactMagnitude: 0.5
      },
      {
        id: '8',
        headline: 'Consumer Spending Data Shows Mixed Signals',
        summary: 'Latest consumer spending data indicates potential slowdown in discretionary spending, which could impact TSLA, AMZN, and HD performance.',
        source: 'Bloomberg',
        sourceUrl: 'https://bloomberg.com/consumer-spending',
        publishedAt: new Date(Date.now() - 4 * 60 * 60 * 1000),
        relevanceScore: 0.68,
        urgencyLevel: 'low',
        timeHorizon: 'short-term',
        sectorTags: ['Consumer Discretionary'],
        impactMagnitude: 0.4
      },
      {
        id: '9',
        headline: 'Financial Sector Earnings Beat Expectations',
        summary: 'JPM and V report strong quarterly results, driven by improved lending margins and payment processing growth.',
        source: 'CNBC',
        sourceUrl: 'https://cnbc.com/financial-earnings',
        publishedAt: new Date(Date.now() - 5 * 60 * 60 * 1000),
        relevanceScore: 0.75,
        urgencyLevel: 'medium',
        timeHorizon: 'short-term',
        sectorTags: ['Financial Services'],
        impactMagnitude: 0.6
      },
      {
        id: '10',
        headline: 'Market Volatility Index Hits 3-Month High',
        summary: 'VIX index reaches highest level in three months, indicating increased market uncertainty and potential for higher volatility across all sectors.',
        source: 'MarketWatch',
        sourceUrl: 'https://marketwatch.com/vix-volatility',
        publishedAt: new Date(Date.now() - 6 * 60 * 60 * 1000),
        relevanceScore: 0.82,
        urgencyLevel: 'high',
        timeHorizon: 'immediate',
        sectorTags: ['Market'],
        impactMagnitude: 0.8
      }
    ];
  },

  async fetchRiskAlerts(userId: string): Promise<RiskAlert[]> {
    void userId;
    await new Promise(resolve => setTimeout(resolve, 600));
    
    return [
      {
        id: '1',
        alertType: 'volatility_spike',
        severity: 'high',
        title: 'Fed Policy Impact on Tech Holdings',
        description: 'Upcoming Fed rate decision could significantly impact your technology holdings (AAPL, MSFT, GOOGL, NVDA, META). Tech stocks are sensitive to interest rate changes and may experience increased volatility.',
        affectedPositions: ['AAPL', 'MSFT', 'GOOGL', 'NVDA', 'META'],
        estimatedImpact: {
          dollarAmount: 6500,
          percentageOfPortfolio: 7.4
        },
        createdAt: new Date(Date.now() - 15 * 60 * 1000)
      },
      {
        id: '2',
        alertType: 'correlation_risk',
        severity: 'high',
        title: 'High Correlation Between Tech Holdings',
        description: 'Your technology holdings (AAPL, MSFT, GOOGL, NVDA, META) show 0.85 correlation, amplifying sector-specific risks.',
        affectedPositions: ['AAPL', 'MSFT', 'GOOGL', 'NVDA', 'META'],
        estimatedImpact: {
          dollarAmount: 6500,
          percentageOfPortfolio: 7.4
        },
        createdAt: new Date(Date.now() - 3 * 60 * 60 * 1000)
      },

              {
          id: '3',
          alertType: 'volatility_spike',
          severity: 'medium',
          title: 'Earnings Season Impact',
          description: 'Upcoming earnings reports for AAPL, MSFT, and GOOGL could create significant portfolio volatility given their combined 41% weight.',
          affectedPositions: ['AAPL', 'MSFT', 'GOOGL'],
          estimatedImpact: {
            dollarAmount: 3600,
            percentageOfPortfolio: 4.1
          },
          createdAt: new Date(Date.now() - 10 * 60 * 60 * 1000)
        },
        {
          id: '4',
          alertType: 'volatility_spike',
          severity: 'high',
          title: 'Oil Price Volatility Impact',
          description: 'Rising oil prices could impact transportation costs for AMZN, TSLA, and HD, while benefiting energy sector holdings.',
          affectedPositions: ['AMZN', 'TSLA', 'HD'],
          estimatedImpact: {
            dollarAmount: 3200,
            percentageOfPortfolio: 3.7
          },
          createdAt: new Date(Date.now() - 15 * 60 * 60 * 1000)
        },
        {
          id: '5',
          alertType: 'correlation_risk',
          severity: 'medium',
          title: 'Inflation Data Release',
          description: 'Upcoming CPI data release could trigger market-wide volatility, particularly affecting growth stocks and tech holdings.',
          affectedPositions: ['AAPL', 'MSFT', 'GOOGL', 'NVDA', 'META', 'TSLA'],
          estimatedImpact: {
            dollarAmount: 2800,
            percentageOfPortfolio: 3.2
          },
          createdAt: new Date(Date.now() - 16 * 60 * 60 * 1000)
        },
        {
          id: '6',
          alertType: 'sector_overexposure',
          severity: 'medium',
          title: 'Geopolitical Tensions',
          description: 'Escalating geopolitical tensions could impact global supply chains, affecting technology and consumer discretionary sectors.',
          affectedPositions: ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'TSLA'],
          estimatedImpact: {
            dollarAmount: 2400,
            percentageOfPortfolio: 2.7
          },
          createdAt: new Date(Date.now() - 17 * 60 * 60 * 1000)
        },
        {
          id: '7',
          alertType: 'concentration_risk',
          severity: 'medium',
          title: 'Technology Sector Concentration',
          description: 'Your portfolio has 57% exposure to technology sector, which remains elevated despite recent diversification.',
          affectedPositions: ['AAPL', 'MSFT', 'GOOGL', 'NVDA', 'META'],
          estimatedImpact: {
            dollarAmount: 4200,
            percentageOfPortfolio: 4.8
          },
          createdAt: new Date(Date.now() - 45 * 60 * 1000)
        }
    ];
  },

  async fetchOpportunities(userId: string): Promise<OpportunityInsight[]> {
    void userId;
    await new Promise(resolve => setTimeout(resolve, 700));
    
    return [
      {
        id: '1',
        opportunityType: 'sector_rotation',
        confidence: 0.82,
        title: 'Reduce Tech Correlation Risk',
        description: 'Analyze diversifying within technology sector to reduce correlation risk between holdings.',
        potentialBenefit: {
          safetyScoreImprovement: 3,
          percentageOfPortfolio: 3.7
        },
        alignsWithArchetype: true
      },
      {
        id: '2',
        opportunityType: 'defensive_move',
        confidence: 0.75,
        title: 'Add Defensive Positions',
        description: 'Analyze adding utilities or consumer staples to reduce portfolio volatility.',
        potentialBenefit: {
          safetyScoreImprovement: 5,
          percentageOfPortfolio: 2.1
        },
        alignsWithArchetype: false
      },
      {
        id: '3',
        opportunityType: 'momentum_play',
        confidence: 0.68,
        title: 'Healthcare Momentum Opportunity',
        description: 'Healthcare sector showing positive momentum. Analyze increasing JNJ or adding new healthcare positions.',
        potentialBenefit: {
          safetyScoreImprovement: 2,
          percentageOfPortfolio: 2.5
        },
        alignsWithArchetype: true
      },
      {
        id: '4',
        opportunityType: 'defensive_move',
        confidence: 0.71,
        title: 'Add International Exposure',
        description: 'Adding international holdings could improve diversification and reduce geographic concentration risk.',
        potentialBenefit: {
          safetyScoreImprovement: 4,
          percentageOfPortfolio: 1.8
        },
        alignsWithArchetype: false
      },
      {
        id: '5',
        opportunityType: 'sector_rotation',
        confidence: 0.65,
        title: 'Balance Growth with Value',
        description: 'Analyze adding value stocks to balance the current growth-heavy portfolio allocation.',
        potentialBenefit: {
          safetyScoreImprovement: 3,
          percentageOfPortfolio: 2.3
        },
        alignsWithArchetype: false
      },

      {
        id: '6',
        opportunityType: 'defensive_move',
        confidence: 0.68,
        title: 'Add Dividend Stocks',
        description: 'Analyze adding dividend-paying stocks to provide income and reduce portfolio volatility.',
        potentialBenefit: {
          safetyScoreImprovement: 2,
          percentageOfPortfolio: 1.9
        },
        alignsWithArchetype: false
      },
      {
        id: '7',
        opportunityType: 'sector_rotation',
        confidence: 0.64,
        title: 'Energy Sector Opportunity',
        description: 'Energy sector showing value opportunities. Analyze adding energy exposure for diversification.',
        potentialBenefit: {
          safetyScoreImprovement: 3,
          percentageOfPortfolio: 2.2
        },
        alignsWithArchetype: false
      }
    ];
  },

  async fetchDailyBriefing(userId: string): Promise<DailyBriefing> {
    void userId;
    await new Promise(resolve => setTimeout(resolve, 900));
    
    return {
      id: '1',
      headline: 'Fed Policy Uncertainty Amplifies Tech Concentration Risk',
      summary: 'Your portfolio faces increased volatility risk as Fed policy decisions loom over your 57% technology exposure. While recent diversification into healthcare and financial services provides some balance, the high correlation between your major tech holdings (AAPL, MSFT, GOOGL, NVDA, META) creates concentrated sector risk that could amplify any negative Fed policy impacts.',
      keyInsights: [
        'Fed rate decision expected this week - tech stocks highly sensitive to policy changes',
        'Technology sector at 57% exposure with 0.85 correlation between major holdings',
        'Recent diversification into healthcare (JNJ, UNH) and financial services (JPM, V) provides some balance',
        'Consumer discretionary exposure at 18% (TSLA, AMZN, HD) - monitor economic cycle sensitivity',
        'Portfolio beta of 1.12 indicates above-market volatility, requiring careful risk management',
        'High correlation risk between tech holdings could amplify sector-specific volatility'
      ],
      riskHighlight: 'Fed policy uncertainty combined with 57% tech exposure and 0.85 correlation between major tech holdings creates concentrated sector risk that could lead to amplified volatility',
      opportunityHighlight: 'Analyze defensive positioning and sector rotation opportunities to reduce correlation risk while maintaining growth exposure',
      actionableItems: [
        'Monitor Fed policy announcements closely - tech holdings may experience increased volatility',
        'Analyze reducing tech correlation by diversifying within the technology sector',
        'Add defensive positions in utilities or consumer staples to reduce portfolio volatility',
        'Evaluate international diversification opportunities to reduce geographic concentration',
        'Review consumer discretionary exposure given economic uncertainty and potential spending slowdown',
        'Analyze value stock additions to balance current growth-heavy allocation'
      ],
      tone: 'cautious',
      generatedAt: new Date().toISOString(),
      confidence: 0.88
    };
  }
};

// Utility functions
const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const formatPercent = (value: number) => {
  return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
};

const getRiskColor = (score: number) => {
  if (score >= 80) return 'text-green-400';
  if (score >= 60) return 'text-yellow-400';
  if (score >= 40) return 'text-orange-400';
  return 'text-red-400';
};

const getUrgencyColor = (urgency: string) => {
  switch (urgency) {
    case 'critical': return 'bg-red-500 text-white';
    case 'high': return 'bg-orange-500 text-white';
    case 'medium': return 'bg-yellow-500 text-black';
    case 'low': return 'bg-green-500 text-white';
    default: return 'bg-slate-500 text-white';
  }
};

const getTimeAgo = (date: Date) => {
  const now = new Date();
  const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
  
  if (diffInHours < 1) return 'Just now';
  if (diffInHours < 24) return `${diffInHours}h ago`;
  const diffInDays = Math.floor(diffInHours / 24);
  return `${diffInDays}d ago`;
};



// Profile Summary Component
const ProfileSummary: React.FC<{ profile: InvestorProfile }> = ({ profile }) => {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-white font-medium mb-2">Investor Profile</h3>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-slate-400 text-sm">Archetype:</span>
            <span className="text-white text-sm font-medium capitalize">{profile.investorArchetype}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400 text-sm">Risk Tolerance:</span>
            <span className="text-white text-sm font-medium capitalize">{profile.riskTolerance}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400 text-sm">Beta:</span>
            <span className="text-white text-sm font-medium">{profile.portfolioBeta.toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400 text-sm">Confidence:</span>
            <span className="text-white text-sm font-medium">{Math.round(profile.profileConfidence * 100)}%</span>
          </div>
        </div>
      </div>
      
      <div>
        <h4 className="text-slate-300 font-medium mb-2">AI Summary</h4>
        <p className="text-slate-400 text-sm">{profile.aiSummary}</p>
      </div>
    </div>
  );
};

// Asset Allocation Chart Component
const AssetAllocationChart: React.FC<{ allocation: InvestorProfile['assetAllocation'] }> = ({ allocation }) => {
  const allocationData = [
    { name: 'Equity', value: allocation.equity, color: '#3B82F6' },
    { name: 'Bond', value: allocation.bond, color: '#10B981' },
    { name: 'Alternative', value: allocation.alternative, color: '#F59E0B' },
    { name: 'Cash', value: allocation.cash, color: '#6B7280' }
  ];

  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-white font-medium mb-2">Asset Allocation</h4>
        <ResponsiveContainer width="100%" height={150}>
          <PieChart>
            <Pie
              data={allocationData}
              cx="50%"
              cy="50%"
              innerRadius={30}
              outerRadius={60}
              paddingAngle={2}
              dataKey="value"
            >
              {allocationData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
        <div className="grid grid-cols-2 gap-2 mt-2">
          {allocationData.map((item, index) => (
            <div key={index} className="flex items-center justify-between text-xs">
              <div className="flex items-center">
                <div 
                  className="w-3 h-3 rounded mr-2" 
                  style={{ backgroundColor: item.color }}
                ></div>
                <span className="text-slate-300">{item.name}</span>
              </div>
              <span className="text-white font-medium">{item.value}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Sector Exposure Chart Component
const SectorExposureChart: React.FC<{ sectorExposure: Record<string, number> }> = ({ sectorExposure }) => {
  const sectorColors = [
    '#3B82F6', // Blue
    '#10B981', // Green
    '#F59E0B', // Amber
    '#EF4444', // Red
    '#8B5CF6', // Purple
    '#06B6D4', // Cyan
    '#F97316', // Orange
    '#84CC16', // Lime
    '#EC4899', // Pink
    '#6366F1'  // Indigo
  ];

  const sectorData = Object.entries(sectorExposure)
    .map(([sector, percentage], index) => ({
      name: sector,
      value: percentage,
      color: sectorColors[index % sectorColors.length]
    }))
    .sort((a, b) => b.value - a.value); // Sort by percentage descending

  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-white font-medium mb-2">Sector Exposure</h4>
        <ResponsiveContainer width="100%" height={150}>
          <PieChart>
            <Pie
              data={sectorData}
              cx="50%"
              cy="50%"
              innerRadius={30}
              outerRadius={60}
              paddingAngle={2}
              dataKey="value"
            >
              {sectorData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
        <div className="grid grid-cols-1 gap-2 mt-2">
          {sectorData.map((item, index) => (
            <div key={index} className="flex items-center justify-between text-xs">
              <div className="flex items-center">
                <div 
                  className="w-3 h-3 rounded mr-2" 
                  style={{ backgroundColor: item.color }}
                ></div>
                <span className="text-slate-300">{item.name}</span>
              </div>
              <span className="text-white font-medium">{item.value}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Holdings Table Component
const HoldingsTable: React.FC<{ holdings: Holding[]; onStockClick?: (holding: Holding) => void }> = ({ holdings, onStockClick }) => {



  const sortedHoldings = [...holdings];

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-700">
            <th className="text-left py-2 text-slate-400 font-medium">Ticker</th>
            <th className="text-left py-2 text-slate-400 font-medium">Name</th>
            <th className="text-right py-2 text-slate-400 font-medium">Value</th>
            <th className="text-right py-2 text-slate-400 font-medium">Weight</th>
            <th className="text-right py-2 text-slate-400 font-medium">Today</th>
          </tr>
        </thead>
        <tbody>
          {sortedHoldings.map(holding => (
            <tr 
              key={holding.id} 
              className={`border-b border-slate-700/50 hover:bg-slate-700/30 ${
                holding.ticker === 'AAPL' && onStockClick ? 'cursor-pointer' : ''
              }`}
              onClick={() => holding.ticker === 'AAPL' && onStockClick && onStockClick(holding)}
            >
              <td className="py-3 text-white font-medium">{holding.ticker}</td>
              <td className="py-3 text-slate-300">{holding.name}</td>
              <td className="py-3 text-right text-white">{formatCurrency(holding.marketValue)}</td>
              <td className="py-3 text-right text-slate-300">{(holding.weight * 100).toFixed(1)}%</td>
              <td className={`py-3 text-right font-medium ${
                holding.dayChange >= 0 ? 'text-green-400' : 'text-red-400'
              }`}>
                {formatCurrency(holding.dayChange)} ({formatPercent(holding.dayChangePercent)})
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default PortfolioDashboard; 